import React, { useState } from "react";
import {
  Bell,
  Moon,
  IndianRupee,
  Headphones,
  DatabaseZap,
} from "lucide-react";
import { createAvatar } from "@dicebear/core";
import { initials } from "@dicebear/collection";
import { NotificationBadge } from "../ui/notification-badge";
import { useTheme } from "@/contexts/ThemeContext";
import { useAppSelector } from "@/store/hooks";
import { useParams } from "react-router-dom";
import { SSEStatusIndicator } from "@/components/common/SSEStatusIndicator";
import { StandardButton } from "../common/StandardButton";
import { WalletPopover } from "@/components/popups/WalletPopover";
import { SupportPopover } from "@/components/popups/SupportPopover";
import { NotificationPanel } from "@/components/notifications/NotificationPanel";
import { invalidateCatalogCache } from "@/services/productService";
import { toast } from "sonner";
import { ApiError } from "@/lib/apiClient";
import { useAuth } from "@/hooks/useAuth";

const Header = () => {
  const { isDark, toggleTheme } = useTheme();
  const params = useParams();
  const { currentStore } = useAuth();
  const { unreadCount } = useAppSelector((state) => state.notifications);
  const [notificationPanelOpen, setNotificationPanelOpen] = useState(false);
  const [cacheInvalidating, setCacheInvalidating] = useState(false);

  const storeId = currentStore?.storeId || (params?.storeId as string);

  const storeName = currentStore?.storeName || "Store";

  // Generate a DiceBear avatar as a data URI when no logo is provided
  const avatarSeed =
    currentStore?.storeId || currentStore?.storeName || "store";
  const generatedAvatar = createAvatar(initials, {
    seed: avatarSeed,
  }).toDataUri();

  const handleInvalidateCache = async () => {
    if (!storeId) {
      toast.error("No store selected");
      return;
    }
    setCacheInvalidating(true);
    try {
      const res = await invalidateCatalogCache();
      if (res.valid) {
        toast.success(res.message || "Storefront cache cleared");
      } else {
        toast.error(res.message || "Failed to clear cache");
      }
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : "Failed to clear cache";
      toast.error(msg);
    } finally {
      setCacheInvalidating(false);
    }
  };

  return (
    <div className="sticky top-0 px-2.5 py-2.5 z-50 animate-in slide-in-from-top duration-500">
      <div className="bg-secondary-background header-customer-shadow border rounded-full p-[6px] flex items-center justify-between h-[62px]">
        <div className="p-[6px] space-x-2 flex items-center">
          <div className="h-[38px] w-[38px]">
            {currentStore?.logo && typeof currentStore.logo === "string" ? (
              <img
                className="h-full w-full rounded-full object-cover"
                src={currentStore.logo}
                alt="store-logo"
              />
            ) : (
              <img
                className="h-full w-full rounded-full object-cover"
                src={generatedAvatar}
                alt="store-logo"
              />
            )}
          </div>
          <div className="pr-2">
            <p className="font-medium">{storeName}</p>
            {currentStore?.domain && (
              <p className="text-xs text-muted-foreground">
                {currentStore.domain}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 pr-3">
          <StandardButton
            shape="pill"
            variant="secondary"
            disabled={cacheInvalidating || !storeId}
            aria-label={
              cacheInvalidating
                ? "Clearing storefront cache"
                : "Clear storefront catalog and price cache"
            }
            title="Clear catalog and price caches so the storefront shows latest products immediately"
            onClick={handleInvalidateCache}
          >
            <DatabaseZap className="!h-5 !w-5 shrink-0" />
            <span className="hidden sm:inline">
              {cacheInvalidating ? "Clearing…" : "Clear cache"}
            </span>
          </StandardButton>

          <WalletPopover>
            <StandardButton shape="pill" variant="secondary">
              <IndianRupee className="!h-5 !w-5" />
              Wallet
            </StandardButton>
          </WalletPopover>

          <SupportPopover>
            <StandardButton shape="pill" variant="secondary">
              <Headphones className="!h-5 !w-5" />
              Support
            </StandardButton>
          </SupportPopover>

          <div className="h-[14px] w-px bg-foreground mx-5" />
          <SSEStatusIndicator />
          <button
            className="h-[26px] w-[26px]"
            aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
            onClick={toggleTheme}
          >
            <Moon className="w-full h-full" />
          </button>
          <NotificationBadge
            className="bg-notification-error"
            max={99}
            label={unreadCount}
            show={unreadCount > 0}
          >
            <button
              className="h-[26px] w-[26px]"
              onClick={() => setNotificationPanelOpen(true)}
              aria-label="Open notifications"
            >
              <Bell className="w-full h-full" />
            </button>
          </NotificationBadge>
        </div>
      </div>

      {/* Notification Panel */}
      <NotificationPanel
        open={notificationPanelOpen}
        onOpenChange={setNotificationPanelOpen}
      />
    </div>
  );
};

export default Header;
